import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from './Modal'
import { Input } from './Input'
import { Search, Check } from 'lucide-react'

interface SelectModalProps<T> {
  isOpen: boolean
  onClose: () => void
  onSelect: (item: T) => void
  items: T[]
  title: string
  searchPlaceholder?: string
  getItemKey: (item: T) => string
  getItemLabel: (item: T) => string
  getItemDescription?: (item: T) => string
  selectedId?: string
  itemsPerPage?: number
}

export function SelectModal<T>({
  isOpen,
  onClose,
  onSelect,
  items,
  title,
  searchPlaceholder = 'Buscar...',
  getItemKey,
  getItemLabel,
  getItemDescription,
  selectedId,
  itemsPerPage = 50,
}: SelectModalProps<T>) {
  const [search, setSearch] = useState('')
  const [displayedItems, setDisplayedItems] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const filteredItems = items.filter(item => {
    const label = getItemLabel(item)?.toLowerCase() || ''
    const description = getItemDescription?.(item)?.toLowerCase() || ''
    const searchLower = search.toLowerCase()
    return label.includes(searchLower) || description.includes(searchLower)
  })

  useEffect(() => {
    setPage(1)
    setDisplayedItems(filteredItems.slice(0, itemsPerPage))
  }, [search, items])

  const loadMore = useCallback(() => {
    const nextPage = page + 1
    const startIndex = 0
    const endIndex = nextPage * itemsPerPage
    setDisplayedItems(filteredItems.slice(startIndex, endIndex))
    setPage(nextPage)
  }, [page, filteredItems, itemsPerPage])

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && displayedItems.length < filteredItems.length) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [displayedItems.length, filteredItems.length, loadMore])

  const handleSelect = (item: T) => {
    onSelect(item)
    onClose()
    setSearch('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          {displayedItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Nenhum item encontrado
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayedItems.map(item => {
                const key = getItemKey(item)
                const isSelected = selectedId === key

                return (
                  <button
                    key={key}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center justify-between p-4 min-h-[60px] text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors active:scale-[0.98] ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-base text-gray-900 dark:text-white truncate">
                        {getItemLabel(item)}
                      </p>
                      {getItemDescription && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {getItemDescription(item)}
                        </p>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="h-6 w-6 text-primary-600 dark:text-primary-400 ml-3 flex-shrink-0" />
                    )}
                  </button>
                )
              })}

              {displayedItems.length < filteredItems.length && (
                <div ref={loadMoreRef} className="p-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Mostrando {displayedItems.length} de {filteredItems.length} itens
        </p>
      </div>
    </Modal>
  )
}
